import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Grid, Box, Paper, TextField, Button,
    Snackbar, Alert, CircularProgress, MenuItem, Tooltip, Tabs, Tab,
    Card, CardContent, Divider, LinearProgress, Chip
} from '@mui/material';
import {
    Search, Send, Business, Schedule, Place, Mail, Gavel, Assignment,
    BarChart, Description, Timeline, Reply, Person, AssignmentTurnedIn, AssignmentLate, Download
} from '@mui/icons-material';
import SidebarNavigation from '../utils/SideBarNavigation';
import firebaseApp, { cidade, KEY_REF } from '../adm/admin/firebaseConfig';
import { getDatabase, ref, push, get, update } from 'firebase/database';
import TitleSection from '../utils/TittleTheme';
import { getConfig } from '../adm/ConfigContext';

const ESICPage = () => {
    const config = getConfig();
    const [currentTab, setCurrentTab] = useState(0); // 0 = Solicitações, 1 = Relatórios Estatísticos

    // Formulário enriquecido com tipo de requerente
    const [formData, setFormData] = useState({ name: '', email: '', applicantType: 'Física', requestType: '', description: '' });
    const [filter, setFilter] = useState('');
    const [ticketNumber, setTicketNumber] = useState(null);
    const [success, setSuccess] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [searchResult, setSearchResult] = useState({ found: false, message: '' });
    const [submissionLimitReached, setSubmissionLimitReached] = useState(false);

    // Estados para Interações/Mensagens de Resposta do Cidadão
    const [interactionText, setInteractionText] = useState('');
    const [sendingInteraction, setSendingInteraction] = useState(false);

    // Estados para Relatórios Estatísticos
    const [allRequests, setAllRequests] = useState([]);
    const [loadingStats, setLoadingStats] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [pntpParams, setPntpParams] = useState(null);

    const databaseInstance = getDatabase(firebaseApp);
    const esicRef = ref(databaseInstance, `esic/`);

    const requestTypes = [
        "Solicitação de Documentos", "Informações sobre Licitações", "Consulta de Tributos", "Serviços de Saúde",
        "Programas Sociais", "Regulamentações de Zoneamento", "Solicitações de Infraestrutura", "Consulta de Processos",
        "Informações sobre Eventos", "Solicitações de Transporte", "Educação e Creches", "Segurança e Vigilância",
        "Políticas Ambientais", "Programas Culturais", "Requisição de Serviços Públicos", "Acesso à Informação Pública",
        "Transparência Fiscal", "Consulta de Orçamentos e Despesas", "Relatórios de Gestão", "Auditoria de Contas Públicas",
        "Denúncias de Corrupção", "Requisição de Acesso a Contratos", "Solicitação de Licenças Ambientais",
        "Solicitação de Alvarás", "Cadastro e Regularização Imobiliária", "Consulta de Aprovação de Projetos",
        "Relatórios de Segurança Urbana", "Pedidos de Manutenção de Vias", "Solicitação de Coleta de Lixo",
        "Fiscalização de Comércio", "Solicitação de Iluminação Pública", "Solicitações de Equipamentos Urbanos",
        "Regularização de Comércio Ambulante", "Informações sobre Patrimônio Cultural", "Consulta de Dados Estatísticos",
        "Relatórios de Impacto Ambiental", "Consulta de Política Habitacional", "Registro de Reclamações"
    ];

    const generateTicketNumber = () => Math.floor(1000000000 + Math.random() * 9000000000);

    useEffect(() => {
        checkSubmissionLimit();
        fetchPntpParameters();
        fetchAllRequestsForStats();
    }, []);

    const fetchPntpParameters = async () => {
        try {
            const paramsRef = ref(databaseInstance, 'config_parametros/esic');
            const snapshot = await get(paramsRef);
            if (snapshot.exists()) {
                const data = snapshot.val();
                const hasData = Object.values(data).some(val => val && val.trim() !== "");
                if (hasData) setPntpParams(data);
            }
        } catch (error) {
            console.error("Erro ao carregar parâmetros institucionais:", error);
        }
    };

    const fetchAllRequestsForStats = async () => {
        setLoadingStats(true);
        try {
            const snapshot = await get(esicRef);
            if (snapshot.exists()) {
                const data = [];
                snapshot.forEach((child) => {
                    data.push({ id: child.key, ...child.val() });
                });
                setAllRequests(data);
            }
        } catch (error) {
            console.error("Erro ao carregar estatísticas:", error);
        } finally {
            setLoadingStats(false);
        }
    };

    const checkSubmissionLimit = () => {
        const submissionData = JSON.parse(localStorage.getItem('submissionData')) || { count: 0, lastSubmission: null };
        const now = new Date();
        const lastSubmissionDate = new Date(submissionData.lastSubmission);

        if (submissionData.count >= 3 && now - lastSubmissionDate < 24 * 60 * 60 * 1000) {
            setSubmissionLimitReached(true);
        } else if (now - lastSubmissionDate >= 24 * 60 * 60 * 1000) {
            localStorage.setItem('submissionData', JSON.stringify({ count: 0, lastSubmission: now }));
            setSubmissionLimitReached(false);
        }
    };

    const incrementSubmissionCount = () => {
        const submissionData = JSON.parse(localStorage.getItem('submissionData')) || { count: 0, lastSubmission: null };
        submissionData.count += 1;
        submissionData.lastSubmission = new Date();
        localStorage.setItem('submissionData', JSON.stringify(submissionData));
    };

    const validateForm = () => {
        return (
            formData.name.trim() &&
            formData.email.trim() &&
            formData.requestType &&
            formData.description.trim().length >= 10
        );
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            setSearchResult({ found: false, message: "Preencha todos os campos corretamente." });
            return;
        }

        if (submissionLimitReached) return;

        const newTicket = generateTicketNumber();
        const currentDate = new Date();
        setLoading(true);

        const newRequest = {
            ...formData,
            date: currentDate.toLocaleDateString('pt-BR'),
            year: currentDate.getFullYear(),
            month: currentDate.getMonth() + 1,
            ticket: newTicket,
            status: "Criado",
            historico: [
                {
                    data: currentDate.toLocaleString('pt-BR'),
                    titulo: "Solicitação protocolada",
                    descricao: "A sua demanda de acesso à informação foi registrada com sucesso e encaminhada para o setor responsável.",
                    autor: "Cidadão",
                    grauSigilo: "Publico" // Por padrão a abertura é pública
                }
            ]
        };

        await push(esicRef, newRequest);
        setTicketNumber(newTicket);
        incrementSubmissionCount();
        setFormData({ name: '', email: '', applicantType: 'Física', requestType: '', description: '' });
        setSuccess(true);
        setLoading(false);
        checkSubmissionLimit();
        fetchAllRequestsForStats();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFilterSearch = () => {
        setSearching(true);
        setSelectedRequest(null);

        get(esicRef)
            .then((snapshot) => {
                let foundRequest = null;
                snapshot.forEach((childSnapshot) => {
                    const data = childSnapshot.val();
                    if (data.ticket?.toString() === filter.trim()) {
                        foundRequest = { id: childSnapshot.key, ...data };
                    }
                });
                setSearching(false);

                if (foundRequest) {
                    if (!foundRequest.historico) {
                        foundRequest.historico = [
                            { data: foundRequest.date, titulo: "Solicitação Criada", descricao: foundRequest.description, autor: "Cidadão", grauSigilo: "Publico" }
                        ];
                        if (foundRequest.retorno) {
                            foundRequest.historico.push({ data: foundRequest.date, titulo: "Resposta Anterior", descricao: foundRequest.retorno, autor: "Administração", grauSigilo: "Publico" });
                        }
                    }
                    setSelectedRequest(foundRequest);
                    setSearchResult({ found: true, message: "Ticket encontrado!" });
                } else {
                    setSearchResult({ found: false, message: "Ticket não encontrado." });
                }
            })
            .catch((error) => {
                console.error("Erro ao buscar ticket:", error);
                setSearching(false);
                setSearchResult({ found: false, message: "Erro ao buscar ticket." });
            });
    };

    const handleSendInteraction = async () => {
        if (!interactionText.trim()) return;

        setSendingInteraction(true);
        try {
            const updatedHistory = [...(selectedRequest.historico || [])];
            updatedHistory.push({
                data: new Date().toLocaleString('pt-BR'),
                titulo: "Mensagem / Resposta do Cidadão",
                descricao: interactionText,
                autor: "Cidadão",
                grauSigilo: "Publico"
            });

            let newStatus = selectedRequest.status;
            if (["Atendido", "Indeferido", "Aprovado", "Rejeitado"].includes(selectedRequest.status)) {
                newStatus = "Recurso em Análise";
            } else {
                newStatus = "Interação em Análise";
            }

            const targetRef = ref(databaseInstance, `esic/${selectedRequest.id}`);
            await update(targetRef, {
                status: newStatus,
                historico: updatedHistory
            });

            setSelectedRequest(prev => ({
                ...prev,
                status: newStatus,
                historico: updatedHistory
            }));

            setInteractionText('');
            setSearchResult({ found: true, message: "Sua resposta/mensagem foi enviada com sucesso!" });
        } catch (error) {
            console.error("Erro ao enviar mensagem:", error);
            setSearchResult({ found: false, message: "Falha ao registrar resposta." });
        } finally {
            setSendingInteraction(false);
        }
    };

    const getStatsByYear = (year) => {
        const filtered = allRequests.filter(req => req.year === parseInt(year) || req.date?.includes(`/${year}`));
        const total = filtered.length;
        const atendidos = filtered.filter(req => req.status === "Atendido" || req.status === "Aprovado").length;
        const indeferidos = filtered.filter(req => req.status === "Indeferido" || req.status === "Rejeitado").length;
        const emAndamento = total - (atendidos + indeferidos);

        return { total, atendidos, indeferidos, emAndamento };
    };

    const stats = getStatsByYear(selectedYear);
    const uniqueYears = [...new Set(allRequests.map(req => req.year || (req.date ? parseInt(req.date.split('/')[2]) : null)))].filter(Boolean).sort((a, b) => b - a);
    if (uniqueYears.length === 0) uniqueYears.push(new Date().getFullYear());

    // Filtros lógicos para o Rol de Informações Classificadas e Desclassificadas (Últimos 12 meses)
    const dozeMesesAtras = new Date();
    dozeMesesAtras.setMonth(dozeMesesAtras.getMonth() - 12);

    const documentosClassificados = allRequests.filter(req => 
        req.historico?.some(h => ['Reservado', 'Secreto', 'Ultra-secreto'].includes(h.grauSigilo))
    );

    const documentosDesclassificadosRecentes = allRequests.filter(req => 
        req.historico?.some(h => 
            h.grauSigilo === 'Desclassificado' && 
            h.dataAlteracaoSigilo && new Date(h.dataAlteracaoSigilo) >= dozeMesesAtras
        )
    );

    return (
        <Container maxWidth="lg" sx={{ py: 5, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 4 }}>
                <TitleSection title={`E-SIC`} subtitle="Sistema Eletrônico do Serviço de Informações ao Cidadão" />

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={currentTab} onChange={(e, val) => setCurrentTab(val)} indicatorColor="primary" textColor="primary">
                        <Tab label="Solicitações e Consultas" icon={<Description />} iconPosition="start" />
                        <Tab label="Relatório Estatístico Anual" icon={<BarChart />} iconPosition="start" />
                    </Tabs>
                </Box>

                {currentTab === 0 ? (
                    <>
                        {pntpParams && (
                            <Box mb={4}>
                                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', borderBottom: `2px solid ${config?.color || '#1976d2'}`, pb: 1 }}>
                                    Informações Institucionais e Regulamentação (Atendimento à LAI)
                                </Typography>
                                <Grid container spacing={2}>
                                    {(pntpParams.unidadeResponsavel || pntpParams.responsavelNome) && (
                                        <Grid item xs={12} sm={6}>
                                            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                                                <CardContent sx={{ display: 'flex', gap: 2 }}>
                                                    <Business sx={{ color: config?.color || '#1976d2', mt: 0.5 }} />
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">Unidade Responsável</Typography>
                                                        <Typography variant="body2">{pntpParams.unidadeResponsavel || '-'}</Typography>
                                                        {pntpParams.responsavelNome && (
                                                            <>
                                                                <Typography variant="subtitle2" fontWeight="bold" color="textSecondary" sx={{ mt: 1 }}>Responsável pelo SIC</Typography>
                                                                <Typography variant="body2">{pntpParams.responsavelNome}</Typography>
                                                            </>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    )}

                                    {(pntpParams.enderecoFisico || pntpParams.emailResponsavel || pntpParams.horarioFuncionamento) && (
                                        <Grid item xs={12} sm={6}>
                                            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                                                <CardContent sx={{ display: 'flex', gap: 2 }}>
                                                    <Place sx={{ color: config?.color || '#1976d2', mt: 0.5 }} />
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">Localização e Contato</Typography>
                                                        {pntpParams.enderecoFisico && <Typography variant="body2">{pntpParams.enderecoFisico}</Typography>}
                                                        {pntpParams.emailResponsavel && <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>{pntpParams.emailResponsavel}</Typography>}
                                                        {pntpParams.horarioFuncionamento && <Typography variant="body2" color="textSecondary">{pntpParams.horarioFuncionamento}</Typography>}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    )}

                                    {pntpParams.instrumentoNormativo && (
                                        <Grid item xs={12}>
                                            <Card variant="outlined" sx={{ borderRadius: 2, backgroundColor: '#fdfdfd' }}>
                                                <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                    <Gavel sx={{ color: config?.color || '#1976d2' }} />
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">Regulamentação Local da LAI</Typography>
                                                        <Typography variant="body2">{pntpParams.instrumentoNormativo}</Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    )}

                                    {pntpParams.prazosProcedimentos && (
                                        <Grid item xs={12}>
                                            <Card variant="outlined" sx={{ borderColor: '#e0e0e0', borderRadius: 2, backgroundColor: '#fdfdfd' }}>
                                                <CardContent sx={{ display: 'flex', gap: 2 }}>
                                                    <Assignment sx={{ color: config?.color || '#1976d2', mt: 0.5 }} />
                                                    <Box width="100%">
                                                        <Typography variant="subtitle2" fontWeight="bold" color="textSecondary" mb={0.5}>Prazos, Recursos e Procedimentos ao Cidadão</Typography>
                                                        <Typography variant="body2" color="textPrimary" style={{ whiteSpace: 'pre-line' }}>
                                                            {pntpParams.prazosProcedimentos}
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        )}

                        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Envie sua solicitação de Informação</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Tipo de Requerente"
                                        name="applicantType"
                                        value={formData.applicantType}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="Física">Pessoa Física</MenuItem>
                                        <MenuItem value="Jurídica">Pessoa Jurídica</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <TextField fullWidth label="Nome / Razão Social" name="name" value={formData.name} onChange={handleChange} />
                                </Grid>
                                <Grid item xs={12} md={6} lg={4}>
                                    <TextField fullWidth label="Email" name="email" value={formData.email} onChange={handleChange} />
                                </Grid>
                                <Grid item xs={12} md={6} lg={8}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Assunto / Categoria da Solicitação"
                                        name="requestType"
                                        value={formData.requestType}
                                        onChange={handleChange}
                                    >
                                        {requestTypes.map((type) => (
                                            <MenuItem key={type} value={type}>{type}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Descrição detalhada do Pedido (mínimo 10 caracteres)"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        multiline
                                        rows={4}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Tooltip title={submissionLimitReached ? "Você atingiu o limite diário de 3 solicitações." : ""} placement="top" arrow>
                                        <span>
                                            <Button
                                                variant="contained"
                                                onClick={handleSubmit}
                                                sx={{ backgroundColor: config?.color || '#1976d2', color: 'white', fontWeight: 'bold' }}
                                                disabled={loading || submissionLimitReached}
                                            >
                                                {loading ? <CircularProgress size={24} /> : <Send sx={{ mr: 1, fontSize: 18 }} />}
                                                Protocolar Pedido
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Paper>

                        {ticketNumber && (
                            <Paper elevation={2} sx={{ p: 3, mb: 3, borderLeft: '6px solid green', borderRadius: 2 }}>
                                <Typography variant="h6" color="success.main" fontWeight="bold">Solicitação protocolada com sucesso!</Typography>
                                <Typography variant="body1" sx={{ mt: 1 }}>Número do Ticket: <strong style={{ fontSize: '1.2rem', color: '#1976d2' }}>{ticketNumber}</strong></Typography>
                                <Typography variant="body2" color="textSecondary">Guarde este número com cuidado. Ele é indispensável para consultar o andamento e interpor recursos.</Typography>
                            </Paper>
                        )}

                        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Acompanhar Solicitação Existente</Typography>
                            <Box display="flex" gap={1} mb={1}>
                                <TextField
                                    fullWidth
                                    label="Digite o número do ticket enviado"
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                />
                                <Button variant="contained" onClick={handleFilterSearch} disabled={searching} sx={{ backgroundColor: config?.color || '#1976d2', px: 4 }}>
                                    {searching ? <CircularProgress size={24} /> : <Search />}
                                </Button>
                            </Box>
                        </Paper>

                        {selectedRequest && (
                            <Paper elevation={3} sx={{ p: 4, mt: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                                <Box display="flex" justifyContent="space-between" width="100%" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                                    <Typography variant="h5" sx={{ color: config?.color || '#1976d2', fontWeight: 'bold' }}>
                                        Dossiê do Pedido #{selectedRequest.ticket}
                                    </Typography>
                                    <Box sx={{ p: '6px 16px', backgroundColor: '#e3f2fd', borderRadius: 2, fontWeight: 'bold', color: '#0d47a1' }}>
                                        Status Atual: {selectedRequest.status}
                                    </Box>
                                </Box>

                                <Grid container spacing={2} sx={{ mb: 4, backgroundColor: '#f9f9f9', p: 2, borderRadius: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="textSecondary"><strong>Interessado:</strong> {selectedRequest.name} ({selectedRequest.applicantType})</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="textSecondary"><strong>Data de Entrada:</strong> {selectedRequest.date}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="textSecondary"><strong>Assunto Enquadrado:</strong> {selectedRequest.requestType}</Typography>
                                    </Grid>
                                    
                                    {(selectedRequest.anexo || selectedRequest.urlAnexo) && (
                                        <Grid item xs={12}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                startIcon={<Download />}
                                                href={selectedRequest.anexo || selectedRequest.urlAnexo}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ backgroundColor: config?.color || '#1976d2', fontWeight: 'bold', mt: 1 }}
                                            >
                                                Baixar Anexo Inicial
                                            </Button>
                                        </Grid>
                                    )}
                                </Grid>

                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Timeline /> Histórico de Tramitação e Respostas
                                </Typography>

                                <Box sx={{ pl: 2, borderLeft: '3px solid #e0e0e0', ml: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    {selectedRequest.historico?.map((item, index) => (
                                        <Box key={index} sx={{ position: 'relative' }}>
                                            <Box sx={{
                                                position: 'absolute', left: -25, top: 4, width: 12, height: 12, borderRadius: '50%',
                                                backgroundColor: item.autor === 'Cidadão' ? '#1976d2' : '#2e7d32'
                                            }} />

                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: item.autor === 'Cidadão' ? '#1976d2' : '#2e7d32', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                {item.titulo} — <span style={{ color: 'gray', fontWeight: 'normal' }}>{item.data} ({item.autor})</span>
                                                
                                                {/* Exibição condicional do Grau de Sigilo na Linha do Tempo do Cidadão */}
                                                {item.grauSigilo && (
                                                    <Chip 
                                                        label={item.grauSigilo === 'Publico' ? 'PÚBLICO' : item.grauSigilo.toUpperCase()} 
                                                        size="small"
                                                        color={item.grauSigilo === 'Publico' ? 'success' : item.grauSigilo === 'Desclassificado' ? 'info' : 'error'}
                                                        sx={{ fontWeight: 'bold', height: '18px', fontSize: '0.65rem' }}
                                                    />
                                                )}
                                            </Typography>
                                            
                                            <Typography variant="body2" sx={{ mt: 0.5, backgroundColor: '#fff', p: 1.5, borderRadius: 1, border: '1px solid #f0f0f0', whiteSpace: 'pre-line' }}>
                                                {item.descricao}
                                            </Typography>

                                            {item.arquivoUrl && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        size="small"
                                                        startIcon={<Download />}
                                                        href={item.arquivoUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{ fontWeight: 'bold', textTransform: 'none' }}
                                                    >
                                                        Baixar Anexo da Resposta
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Box>

                                <Box sx={{ mt: 4, p: 3, backgroundColor: '#fcfcfc', borderRadius: 2, border: '1px solid #e3e3e3' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1, color: config?.color || '#1976d2' }}>
                                        <Reply /> Enviar Mensagem / Resposta à Administração
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Utilize este espaço para responder às interações da prefeitura, esclarecer dados ou interpor recursos formais. Sua resposta constará imediatamente na linha do tempo acima.
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        variant="outlined"
                                        placeholder="Digite aqui sua resposta ou manifestação sobre o andamento..."
                                        value={interactionText}
                                        onChange={(e) => setInteractionText(e.target.value)}
                                        sx={{ backgroundColor: '#fff', mb: 2 }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleSendInteraction}
                                        disabled={sendingInteraction || !interactionText.trim()}
                                        sx={{ backgroundColor: config?.color || '#1976d2', color: 'white', fontWeight: 'bold' }}
                                    >
                                        {sendingInteraction ? <CircularProgress size={24} /> : "Enviar Mensagem"}
                                    </Button>
                                </Box>
                            </Paper>
                        )}
                    </>
                ) : (
                    /* ABA 1 - RELATÓRIOS ESTATÍSTICOS EXIGIDOS PELO PNTP */
                    <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Relatório Estatístico de Acesso à Informação</Typography>
                                <Typography variant="body2" color="textSecondary">Cumprimento dos critérios de transparência passiva do PNTP</Typography>
                            </Box>

                            <TextField
                                select
                                label="Selecione o Ano Base"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                                sx={{ minWidth: 150 }}
                            >
                                {uniqueYears.map(yr => (
                                    <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                                ))}
                            </TextField>
                        </Box>

                        {loadingStats ? (
                            <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
                        ) : (
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={4}>
                                    <Card sx={{ backgroundColor: '#edf4fe', borderLeft: '5px solid #1976d2' }}>
                                        <CardContent>
                                            <Typography variant="subtitle2" color="textSecondary" fontWeight="bold">PEDIDOS RECEBIDOS</Typography>
                                            <Typography variant="h3" fontWeight="bold" color="#1976d2" sx={{ my: 1 }}>{stats.total}</Typography>
                                            <Typography variant="caption" color="textSecondary">Total acumulado no ano de {selectedYear}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <Card sx={{ backgroundColor: '#edf7ed', borderLeft: '5px solid #2e7d32' }}>
                                        <CardContent>
                                            <Typography variant="subtitle2" color="textSecondary" fontWeight="bold">PEDIDOS ATENDIDOS</Typography>
                                            <Typography variant="h3" fontWeight="bold" color="#2e7d32" sx={{ my: 1 }}>{stats.atendidos}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {stats.total > 0 ? ((stats.atendidos / stats.total) * 100).toFixed(1) : 0}% de resolutividade positiva
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <Card sx={{ backgroundColor: '#fdeded', borderLeft: '5px solid #d32f2f' }}>
                                        <CardContent>
                                            <Typography variant="subtitle2" color="textSecondary" fontWeight="bold">PEDIDOS INDEFERIDOS</Typography>
                                            <Typography variant="h3" fontWeight="bold" color="#d32f2f" sx={{ my: 1 }}>{stats.indeferidos}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {stats.total > 0 ? ((stats.indeferidos / stats.total) * 100).toFixed(1) : 0}% com recusa fundamentada
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" mb={3}>Demonstrativo Percentual Anual</Typography>

                                        <Box mb={2}>
                                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                <Typography variant="body2" fontWeight="bold">Demandas Atendidas / Concluídas</Typography>
                                                <Typography variant="body2">{stats.atendidos} de {stats.total}</Typography>
                                            </Box>
                                            <LinearProgress variant="determinate" value={stats.total > 0 ? (stats.atendidos / stats.total) * 100 : 0} color="success" sx={{ height: 10, borderRadius: 5 }} />
                                        </Box>

                                        <Box mb={2}>
                                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                <Typography variant="body2" fontWeight="bold">Demandas Indeferidas</Typography>
                                                <Typography variant="body2">{stats.indeferidos} de {stats.total}</Typography>
                                            </Box>
                                            <LinearProgress variant="determinate" value={stats.total > 0 ? (stats.indeferidos / stats.total) * 100 : 0} color="error" sx={{ height: 10, borderRadius: 5 }} />
                                        </Box>

                                        <Box mb={2}>
                                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                <Typography variant="body2" fontWeight="bold">Demandas em Tramitação / Recursos</Typography>
                                                <Typography variant="body2">{stats.emAndamento} de {stats.total}</Typography>
                                            </Box>
                                            <LinearProgress variant="determinate" value={stats.total > 0 ? (stats.emAndamento / stats.total) * 100 : 0} color="warning" sx={{ height: 10, borderRadius: 5 }} />
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* Nova Seção Acoplada: Rol de Informações Classificadas e Desclassificadas */}
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        Transparência Passiva — Rol de Informações Classificadas e Desclassificadas
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                        Classificações de sigilo e reversões aplicadas nos termos da Lei de Acesso à Informação (LAI).
                                    </Typography>
                                    
                                    <Grid container spacing={3}>
                                        {/* Card de Informações Ativamente Classificadas */}
                                        <Grid item xs={12} sm={6}>
                                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', borderLeft: '4px solid #d32f2f', borderRadius: 2 }}>
                                                <Typography variant="subtitle2" fontWeight="bold" color="error" sx={{ mb: 1.5 }}>
                                                    INFORMAÇÕES CLASSIFICADAS ATIVAS
                                                </Typography>
                                                {documentosClassificados.slice(0, 2).map((doc, idx) => (
                                                    <Box key={idx} sx={{ mb: 1, p: 1.5, bgcolor: '#fff', borderRadius: 1, border: '1px solid #eee' }}>
                                                        <Typography variant="caption" fontWeight="bold" color="primary">Ticket: #{doc.ticket}</Typography>
                                                        <Typography variant="body2" color="textPrimary" noWrap>Categoria: {doc.requestType}</Typography>
                                                    </Box>
                                                ))}
                                                {documentosClassificados.length === 0 && (
                                                    <Typography variant="caption" color="textSecondary" display="block" sx={{ fontStyle: 'italic', pt: 1 }}>
                                                        Nenhuma restrição de sigilo ativa cadastrada.
                                                    </Typography>
                                                )}
                                            </Paper>
                                        </Grid>

                                        {/* Card de Informações Desclassificadas nos Últimos 12 Meses */}
                                        <Grid item xs={12} sm={6}>
                                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', borderLeft: '4px solid #0288d1', borderRadius: 2 }}>
                                                <Typography variant="subtitle2" fontWeight="bold" color="info.main" sx={{ mb: 1.5 }}>
                                                    DESCLASSIFICADAS (ÚLTIMOS 12 MESES)
                                                </Typography>
                                                {documentosDesclassificadosRecentes.slice(0, 2).map((doc, idx) => (
                                                    <Box key={idx} sx={{ mb: 1, p: 1.5, bgcolor: '#fff', borderRadius: 1, border: '1px solid #eee' }}>
                                                        <Typography variant="caption" fontWeight="bold" color="primary">Ticket: #{doc.ticket}</Typography>
                                                        <Typography variant="body2" color="textPrimary" noWrap>Item: {doc.requestType}</Typography>
                                                    </Box>
                                                ))}
                                                {documentosDesclassificadosRecentes.length === 0 && (
                                                    <Typography variant="caption" color="textSecondary" display="block" sx={{ fontStyle: 'italic', pt: 1 }}>
                                                        Nenhuma desclassificação registrada nos últimos 12 meses.
                                                    </Typography>
                                                )}
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                    </Paper>
                )}
            </Box>

            <Box sx={{ flex: { xs: 1, md: 1.5 }, minWidth: 250 }}>
                <SidebarNavigation noticia={true} />
            </Box>

            <Snackbar open={success} autoHideDuration={4000} onClose={() => setSuccess(false)}>
                <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
                    Solicitação enviada com sucesso!
                </Alert>
            </Snackbar>

            <Snackbar open={searchResult.message !== ''} autoHideDuration={4000} onClose={() => setSearchResult({ ...searchResult, message: '' })}>
                <Alert onClose={() => setSearchResult({ ...searchResult, message: '' })} severity={searchResult.found ? "success" : "error"} sx={{ width: '100%' }}>
                    {searchResult.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ESICPage;